import {
  Field,
  makeStyles,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Spinner,
  Slot,
  Text,
  Toolbar,
  ToolbarButton,
  ToolbarDivider,
  ToolbarToggleButton,
  Tooltip,
} from '@fluentui/react-components';
import {
  ClipboardPaste24Regular,
  Copy24Regular,
  Cut24Regular,
  Image24Regular,
  Link24Regular,
  LinkDismiss24Regular,
  TextBold24Regular,
  TextBulletListLtr24Regular,
  TextItalic24Regular,
  TextNumberListLtr24Regular,
  TextParagraph24Regular,
  TextQuote24Regular,
  TextUnderline24Regular,
} from '@fluentui/react-icons';
import { Editor } from '@tiptap/core';
import { DOMSerializer } from '@tiptap/pm/model';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import { debounce } from 'es-toolkit';
import { observer } from 'mobx-react-lite';
import React, { ChangeEvent, CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { calculateBytesFromMB } from '../../common/calculation/calculate-bytes-in-mb';
import { ckEditorSupportedDocumentConfiguration } from '../../configurations';
import { isValidImageFile } from '../../shared/functions';
import './RichTextEditor.scss';
import { FlexBox } from './flex-box';
import {
  buildRichTextExtensions,
  exceedsTotalImagesLimit,
  getHtmlImageSizesInBytes,
  HEADING_LEVELS,
  MAX_TOTAL_IMAGES_SIZE_IN_MB,
  type HeadingLevel,
} from './rich-text-editor-utils';

const totalImagesTooLargeNotification = `The total size of the images in the textbox is too large. Please ensure that the total size of all images is no larger than ${MAX_TOTAL_IMAGES_SIZE_IN_MB}MB before submitting.`;

const fileTooLargeNotification = `The files embedded in the textbox are too large, and the nomination cannot be submitted. Please ensure that the images are no larger than ${ckEditorSupportedDocumentConfiguration.maxFileSizeInMB}MB before submitting.`;

const invalidImageFileNotification = 'Select a valid image file.';

const readFileAsDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result as string);
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

interface RichtextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  label?: string;
  inputTitle?: string;
  className?: string;
  maxLength?: number;
  disabled?: boolean;
  height?: number | string;
  errorMessage?: Slot<'div'>;
  onFocus?: () => void;
  onBlur?: () => void;
  showCharacterCounter?: boolean;
  onReady?: () => void;
  resizeEnabled?: boolean;
  showCharacterCounterWithMaxlength?: number;
}

interface RichTextEditorToolbarProps {
  editor: Editor;
  disabled: boolean;
  onInsertImageClick: () => void;
  runWithSpinner: (task: () => Promise<void>) => void;
}

const useStyles = makeStyles({
  disableToolbar: { pointerEvents: 'none' },
  toolbarToggleButton: {
    border: 0,
    borderRadius: 0,
  }
})

const RichTextEditorToolbar: React.FC<RichTextEditorToolbarProps> = (props) => {
  const {
    editor,
    disabled,
    onInsertImageClick,
    runWithSpinner,
  } = props;

  const styles = useStyles();

  const toolbarState = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => ({
      isBold: currentEditor.isActive('bold'),
      isItalic: currentEditor.isActive('italic'),
      isUnderline: currentEditor.isActive('underline'),
      isBulletList: currentEditor.isActive('bulletList'),
      isOrderedList: currentEditor.isActive('orderedList'),
      isBlockquote: currentEditor.isActive('blockquote'),
      isLink: currentEditor.isActive('link'),
      headingLevel: HEADING_LEVELS.find(level => currentEditor.isActive('heading', { level })) ?? null,
    }),
  });

  const checkedValues: Record<string, string[]> = {
    formatting: [
      ...(toolbarState.isBold ? ['bold'] : []),
      ...(toolbarState.isItalic ? ['italic'] : []),
      ...(toolbarState.isUnderline ? ['underline'] : []),
      ...(toolbarState.isBulletList ? ['bulletList'] : []),
      ...(toolbarState.isOrderedList ? ['orderedList'] : []),
      ...(toolbarState.isBlockquote ? ['blockquote'] : []),
    ],
  };

  const handleBold = (): void => {
    editor.chain().focus().toggleBold().run();
  };

  const handleItalic = (): void => {
    editor.chain().focus().toggleItalic().run();
  };

  const handleUnderline = (): void => {
    editor.chain().focus().toggleUnderline().run();
  };

  const handleBulletList = (): void => {
    editor.chain().focus().toggleBulletList().run();
  };

  const handleOrderedList = (): void => {
    editor.chain().focus().toggleOrderedList().run();
  };

  const handleBlockquote = (): void => {
    editor.chain().focus().toggleBlockquote().run();
  };

  const handleParagraph = (): void => {
    editor.chain().focus().setParagraph().run();
  };

  const handleHeading = (level: HeadingLevel): void => {
    editor.chain().focus().toggleHeading({ level }).run();
  };

  const handleLink = (): void => {
    const previousUrl = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Enter a URL', previousUrl ?? 'https://');

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const handleUnlink = (): void => {
    editor.chain().focus().unsetLink().run();
  };

  const writeSelectionToClipboard = async (): Promise<boolean> => {
    const { state } = editor;
    const { from, to, empty } = state.selection;

    if (empty) {
      return false;
    }

    const serializer = DOMSerializer.fromSchema(state.schema);
    const container = document.createElement('div');
    container.appendChild(serializer.serializeFragment(state.selection.content().content));

    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([container.innerHTML], { type: 'text/html' }),
        'text/plain': new Blob([state.doc.textBetween(from, to, '\n')], { type: 'text/plain' }),
      }),
    ]);

    return true;
  };

  const handleCut = (): void => {
    runWithSpinner(async () => {
      editor.chain().focus().run();
      const copied = await writeSelectionToClipboard();

      if (copied) {
        editor.chain().focus().deleteSelection().run();
      }
    });
  };

  const handleCopy = (): void => {
    runWithSpinner(async () => {
      editor.chain().focus().run();
      await writeSelectionToClipboard();
    });
  };

  const handlePaste = (): void => {
    runWithSpinner(async () => {
      if (!navigator.clipboard?.readText) {
        return;
      }

      const text = await navigator.clipboard.readText();

      if (text) {
        editor.chain().focus().insertContent(text).run();
      }
    });
  };

  const formatLabel = toolbarState.headingLevel !== null ? `Heading ${toolbarState.headingLevel}` : 'Paragraph';

  return (
    <Toolbar
      aria-label='Text formatting'
      size='small'
      checkedValues={checkedValues}
      className={disabled ? styles.disableToolbar : ''}
    >
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <ToolbarButton
            aria-label={`Format: ${formatLabel}`}
            disabled={disabled}
            icon={<TextParagraph24Regular />}
          >
            {formatLabel}
          </ToolbarButton>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem onClick={handleParagraph}>Paragraph</MenuItem>
            {HEADING_LEVELS.map(level => (
              <MenuItem
                key={level}
                onClick={() => handleHeading(level)}
              >
                {`Heading ${level}`}
              </MenuItem>
            ))}
          </MenuList>
        </MenuPopover>
      </Menu>

      <ToolbarDivider />

      <Tooltip
        content='Cut'
        relationship='label'
        withArrow
      >
        <ToolbarButton
          aria-label='Cut'
          disabled={disabled}
          icon={<Cut24Regular />}
          onClick={handleCut}
        />
      </Tooltip>
      <Tooltip
        content='Copy'
        relationship='label'
        withArrow
      >
        <ToolbarButton
          aria-label='Copy'
          disabled={disabled}
          icon={<Copy24Regular />}
          onClick={handleCopy}
        />
      </Tooltip>
      <Tooltip
        content='Paste'
        relationship='label'
        withArrow
      >
        <ToolbarButton
          aria-label='Paste'
          disabled={disabled}
          icon={<ClipboardPaste24Regular />}
          onClick={handlePaste}
        />
      </Tooltip>

      <ToolbarDivider />

      <Tooltip
        content='Bold'
        relationship='label'
        withArrow
      >
        <ToolbarToggleButton
          className={styles.toolbarToggleButton}
          aria-label='Bold'
          name='formatting'
          value='bold'
          disabled={disabled}
          icon={<TextBold24Regular />}
          onClick={handleBold}
        />
      </Tooltip>
      <Tooltip
        content='Italic'
        relationship='label'
        withArrow
      >
        <ToolbarToggleButton
          className={styles.toolbarToggleButton}
          aria-label='Italic'
          name='formatting'
          value='italic'
          disabled={disabled}
          icon={<TextItalic24Regular />}
          onClick={handleItalic}
        />
      </Tooltip>

      <Tooltip
        content='Underline'
        relationship='label'
        withArrow
      >
        <ToolbarToggleButton
          className={styles.toolbarToggleButton}
          aria-label='Underline'
          name='formatting'
          value='underline'
          disabled={disabled}
          icon={<TextUnderline24Regular />}
          onClick={handleUnderline}
        />
      </Tooltip>

      <ToolbarDivider />

      <Tooltip
        content='Numbered list'
        relationship='label'
        withArrow
      >
        <ToolbarToggleButton
          className={styles.toolbarToggleButton}
          aria-label='Numbered list'
          name='formatting'
          value='orderedList'
          disabled={disabled}
          icon={<TextNumberListLtr24Regular />}
          onClick={handleOrderedList}
        />
      </Tooltip>
      <Tooltip
        content='Bulleted list'
        relationship='label'
        withArrow
      >
        <ToolbarToggleButton
          className={styles.toolbarToggleButton}
          aria-label='Bulleted list'
          name='formatting'
          value='bulletList'
          disabled={disabled}
          icon={<TextBulletListLtr24Regular />}
          onClick={handleBulletList}
        />
      </Tooltip>
      <Tooltip
        content='Blockquote'
        relationship='label'
        withArrow
      >
        <ToolbarToggleButton
          className={styles.toolbarToggleButton}
          aria-label='Blockquote'
          name='formatting'
          value='blockquote'
          disabled={disabled}
          icon={<TextQuote24Regular />}
          onClick={handleBlockquote}
        />
      </Tooltip>

      <ToolbarDivider />

      <Tooltip
        content='Link'
        relationship='label'
        withArrow
      >
        <ToolbarButton
          aria-label='Link'
          disabled={disabled}
          icon={<Link24Regular />}
          onClick={handleLink}
        />
      </Tooltip>
      <Tooltip
        content='Unlink'
        relationship='label'
        withArrow
      >
        <ToolbarButton
          aria-label='Unlink'
          disabled={disabled || !toolbarState.isLink}
          icon={<LinkDismiss24Regular />}
          onClick={handleUnlink}
        />
      </Tooltip>

      <ToolbarDivider />

      <Tooltip
        content='Image'
        relationship='label'
        withArrow
      >
        <ToolbarButton
          aria-label='Image'
          disabled={disabled}
          icon={<Image24Regular />}
          onClick={onInsertImageClick}
        />
      </Tooltip>
    </Toolbar>
  );
};

const RichTextEditorImpl: React.FC<RichtextEditorProps> = (props) => {
  const {
    value,
    onChange,
    label,
    inputTitle = '',
    className = '',
    disabled = false,
    maxLength,
    errorMessage,
    onBlur,
    onFocus,
    showCharacterCounter = false,
    onReady,
    height,
    resizeEnabled = true,
    showCharacterCounterWithMaxlength,
  } = props;

  const imageFileUploader = useRef<HTMLInputElement | null>(null);
  const editorRef = useRef<Editor | null>(null);
  const [imageWarning, setImageWarning] = useState<string>('');
  const [isProcessing, setProcessing] = useState<boolean>(false);

  const runWithSpinner = (task: () => Promise<void>): void => {
    setProcessing(true);
    task().finally(() => setProcessing(false));
  };

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const appliedValueRef = useRef(value ?? '');

  const emitChange = useMemo(
    () =>
      debounce(() => {
        const currentEditor = editorRef.current;

        if (currentEditor && !currentEditor.isDestroyed) {
          const html = currentEditor.getHTML();
          appliedValueRef.current = html;
          onChangeRef.current?.(html);
        }
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      emitChange.flush();
    };
  }, [emitChange]);

  const insertImageFile = (file: File): void => {
    const editor = editorRef.current;

    if (!editor || !ckEditorSupportedDocumentConfiguration.supportedFileTypes.includes(file.type)) {
      return;
    }

    if (file.size > calculateBytesFromMB(ckEditorSupportedDocumentConfiguration.maxFileSizeInMB)) {
      setImageWarning(fileTooLargeNotification);
      return;
    }

    if (exceedsTotalImagesLimit(editor.getHTML(), [file.size])) {
      setImageWarning(totalImagesTooLargeNotification);
      return;
    }

    setImageWarning('');
    runWithSpinner(async () => {
      const isValidImage = await isValidImageFile(file);
      if (!isValidImage) {
        setImageWarning(invalidImageFileNotification);
        return;
      }

      const dataUrl = await readFileAsDataUrl(file);
      editor.chain().focus().setImage({ src: dataUrl }).run();
    });
  };

  const editor = useEditor({
    extensions: buildRichTextExtensions({ maxLength }),
    content: value ?? '',
    editable: !disabled,
    onCreate: () => {
      onReady && onReady();
    },
    onUpdate: () => {
      emitChange();
    },
    onFocus: () => {
      onFocus && onFocus();
    },
    onBlur: () => {
      emitChange.flush();
      onBlur && onBlur();
    },
    editorProps: {
      attributes: { 'title': label ?? inputTitle },
      handlePaste: (_view, event): boolean => {
        const currentEditor = editorRef.current;
        const imageFiles = Array.from(event.clipboardData?.files ?? []).filter(file => file.type.startsWith('image/'));

        if (imageFiles.length > 0) {
          event.preventDefault();
          insertImageFile(imageFiles[0]);
          return true;
        }

        const pastedHtml = event.clipboardData?.getData('text/html') ?? '';
        const pastedImageSizesInBytes = getHtmlImageSizesInBytes(pastedHtml);

        if (currentEditor && exceedsTotalImagesLimit(currentEditor.getHTML(), pastedImageSizesInBytes)) {
          event.preventDefault();
          setImageWarning(totalImagesTooLargeNotification);
          return true;
        }

        return false;
      },
      handleDrop: (_view, event, _slice, moved): boolean => {
        if (moved) {
          return false;
        }

        const imageFiles = Array.from(event.dataTransfer?.files ?? []).filter(file => file.type.startsWith('image/'));

        if (imageFiles.length === 0) {
          return false;
        }

        event.preventDefault();
        insertImageFile(imageFiles[0]);
        return true;
      },
    },
  });

  editorRef.current = editor;

  const characterCount = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => (currentEditor?.storage.characterCount?.characters() as number | undefined) ?? 0,
  });

  const handleInsertImageClick = (): void => {
    imageFileUploader.current?.click();
  };

  const handleImageInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];

    if (file) {
      insertImageFile(file);
    }
  };

  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      return;
    }

    const incomingValue = value ?? '';

    if (incomingValue === appliedValueRef.current) {
      return;
    }

    appliedValueRef.current = incomingValue;
    editor.commands.setContent(incomingValue, { emitUpdate: false });
  }, [editor, value]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    editor.setEditable(!disabled, false);
  }, [editor, disabled]);

  const showCounter = showCharacterCounterWithMaxlength !== undefined || showCharacterCounter;
  const counterMax = showCharacterCounterWithMaxlength;
  const counterText = counterMax !== undefined ? `${characterCount}/${counterMax}` : `${characterCount}`;

  const contentHeight = typeof height === 'number' ? `${height}px` : height ?? '120px';
  const contentStyle: CSSProperties & Record<`--${string}`, string> = {
    '--rte-content-height': contentHeight,
  };

  return (
    <FlexBox
      direction='column'
      className='rich-text-editor'
    >
      {
        label && (
          <Text
            block
            weight='semibold'
          >
            {label}
          </Text>
        )
      }
      <Field
        validationMessage={errorMessage}
        validationMessageIcon={null}
      >
        <FlexBox
          direction='column'
          className={`${resizeEnabled ? 'rich-text-editor--resizable' : ''} ${errorMessage ? 'rich-text-editor--error' : ''} ${disabled ? 'rich-text-editor--readonly' : ''} ${className}`}
        >
          <div className='rich-text-editor__content'>
            {
              !!editor && (
                <RichTextEditorToolbar
                  editor={editor}
                  disabled={disabled}
                  onInsertImageClick={handleInsertImageClick}
                  runWithSpinner={runWithSpinner}
                />
              )
            }
            <EditorContent
              editor={editor}
              className='content-editor'
              style={contentStyle}
            />
            {
              isProcessing && (
                <div className='rich-text-editor__spinner'>
                  <Spinner size='small' />
                </div>
              )
            }
            <input
              hidden
              type='file'
              className='image-file-input'
              ref={imageFileUploader}
              accept={ckEditorSupportedDocumentConfiguration.supportedFileTypes.toString()}
              onClick={() => {
                if (imageFileUploader.current) {
                  imageFileUploader.current.value = '';
                }
              }}
              onChange={handleImageInputChange}
            />
            {
              showCounter && (
                <Text
                  as='p'
                  size={200}
                  block
                  align='end'
                  className='char-counter'
                >
                  {counterText}
                </Text>
              )
            }
          </div>
          {
            !!imageWarning && (
              <Text
                as='p'
                size={200}
                className='text--red'
              >
                {imageWarning}
              </Text>
            )
          }
        </FlexBox>
      </Field>
    </FlexBox>
  );
};

export const RichTextEditor = observer(RichTextEditorImpl);
